import React, { useEffect, useState } from "react";
import axios from "../../axiosConfig";

const API = "/api";

export default function RfpOutlineReview({ jobId, outline: initialOutline, validation, onConfirmed, onToast }) {
  const [sections, setSections] = useState(initialOutline?.sections || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSections(initialOutline?.sections || []);
  }, [initialOutline]);

  const updateSection = (idx, patch) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addSection = () => {
    const n = sections.length + 1;
    setSections((prev) => [
      ...prev,
      { section_id: `s${n}`, title: "", technology: "Not relevant", legacy: "other" },
    ]);
  };

  const save = async (confirmed = false) => {
    setSaving(true);
    try {
      const url = confirmed
        ? `${API}/rfp-jobs/${jobId}/outline/confirm`
        : `${API}/rfp-jobs/${jobId}/outline`;
      const res = confirmed
        ? await axios.post(url)
        : await axios.put(url, { sections, confirmed: false });
      onToast?.success(confirmed ? "Outline confirmed." : "Outline saved.");
      if (confirmed) onConfirmed?.(res.data);
      return res.data;
    } catch (err) {
      onToast?.error(err?.response?.data?.detail || err.message || "Outline save failed");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900";

  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-medium text-amber-950 dark:text-amber-100">Outline review</h3>
          <p className="mt-1 text-sm text-amber-900/90 dark:text-amber-200/90">
            Edit section titles and products, then confirm to continue.
            {validation?.flags?.length ? ` ${validation.flags.length} validation note(s).` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={saving}
            className="rounded-lg border border-amber-400 px-3 py-1.5 text-sm dark:border-amber-700"
            onClick={() => save(false)}
          >
            Save draft
          </button>
          <button
            type="button"
            disabled={saving || !sections.length}
            className="rounded-lg bg-amber-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            onClick={() => save(true)}
          >
            Confirm &amp; continue
          </button>
        </div>
      </div>

      {validation?.flags?.length > 0 && (
        <ul className="mb-3 space-y-1 text-xs text-amber-900 dark:text-amber-200">
          {validation.flags.map((f, i) => (
            <li key={i}>
              • {f.message || f}
            </li>
          ))}
        </ul>
      )}

      <ul className="space-y-2">
        {sections.map((sec, idx) => (
          <li
            key={sec.section_id || idx}
            className="grid gap-2 rounded-lg border border-amber-200/80 bg-white/70 p-3 sm:grid-cols-[6rem_1fr_10rem] dark:border-amber-900/30 dark:bg-slate-900/50"
          >
            <input
              className={inputCls}
              value={sec.section_id || ""}
              placeholder="ID"
              onChange={(e) => updateSection(idx, { section_id: e.target.value })}
            />
            <input
              className={inputCls}
              value={sec.title || ""}
              placeholder="Section title"
              onChange={(e) => updateSection(idx, { title: e.target.value })}
            />
            <input
              className={inputCls}
              value={sec.technology || ""}
              placeholder="Product"
              onChange={(e) => updateSection(idx, { technology: e.target.value })}
            />
          </li>
        ))}
      </ul>

      <button type="button" className="mt-3 text-sm text-amber-800 underline dark:text-amber-200" onClick={addSection}>
        + Add section
      </button>
    </div>
  );
}
